/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  createFileRoute,
  notFound,
  redirect,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { useAuth, useWhatsNearbyRoute, useI18N } from "common/hooks";
import { useEffect, useState } from "react";
import { useGeoLocation } from "@/hooks/useGeoLocation";
import { defaultRegion } from "common/config";
import Loader from "@/components/common/Loader";
import { ClientOnly } from "@/components/common/ClientOnly";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import { Storage } from "common/storage";
import { createPageAnalytics } from "common/analytics";
import { NotFoundError, SlugRedirectError } from "common/apis/venueDetails";
import { whatsNearbyRouteQueryOptions } from "common/hooks/business/details/venueDetailsQueryOptions";
import type { VenueDetailsBySlugResponse } from "common/hooks/business/details/useVenueDetailsBySlug";
import { nearbyBusinessesQueryOptions } from "common/hooks/business/useNearbyBusinesses";
import {
  buildWhatsNearbyHead,
  venuePageHref,
  type WhatsNearbyListVenue,
  type WhatsNearbyLoaderData,
} from "@/lib/whatsNearbyHead";

const areaMapA = createPageAnalytics("area_map");
import WhatsNearbyContent from "../components/WhatsNearby/WhatsNearbyContent";
import { cn, getVenueSlugs } from "@/lib/utils";

const isProduction =
  import.meta.env.VITE_APPLICATION_ENVIRONMENT === "Production";

export type WhatsNearbyRouteParams = {
  venueId: string; // from the route path"/whats-nearby/$venueId"
};

export type WhatsNearbyRouteSearch = {
  source?: "map" | "activity" | "places";
  showAuthModal?: boolean;
  cid?: string;
  tid?: string;
  venue?: string;
};

export const Route = createFileRoute("/whats-nearby/$venueId")({
  ssr: true,
  component: WhatsNearby,
  // In-app navigations wait on the loader below; a server render never shows this.
  pendingComponent: WhatsNearbyPending,
  pendingMs: 200,
  validateSearch: (search: Record<string, unknown>): WhatsNearbyRouteSearch => {
    return {
      source: search.source as "map" | "activity" | "places",
      showAuthModal: search.showAuthModal as boolean,
      cid: search.cid as string,
      tid: search.tid as string,
      venue: search.venue as string | undefined,
    };
  },
  loader: async ({ params, context, location }): Promise<WhatsNearbyLoaderData> => {
    const canonicalOrigin =
      (typeof process !== "undefined" && process.env?.PUBLIC_ORIGIN) || "";
    const queryOpts = whatsNearbyRouteQueryOptions({
      slug: params.venueId,
      latitude: defaultRegion.latitude.toString(),
      longitude: defaultRegion.longitude.toString(),
      accessToken: undefined,
    });

    let venue: VenueDetailsBySlugResponse | null = null;
    try {
      venue = await context.queryClient.ensureQueryData(queryOpts);
    } catch (err) {
      if (err instanceof NotFoundError) throw notFound();
      if (err instanceof SlugRedirectError)
        throw redirect({
          to: `/whats-nearby/${err.canonicalSlug}`,
          search: location.search as any,
          statusCode: 301,
        });
      // Render client-side as before; a cached error entry would block the browser's retry.
      context.queryClient.removeQueries({
        queryKey: queryOpts.queryKey,
        exact: true,
      });
      console.error("[whats-nearby loader] fetch failed; rendering client-side", err);
    }

    let listVenues: WhatsNearbyListVenue[] = [];
    if (venue?.details?.isAreaMap) {
      const nearbyOpts = nearbyBusinessesQueryOptions(
        String(venue.details.business.id),
      );
      try {
        const nearby = await context.queryClient.ensureQueryData(nearbyOpts);
        listVenues = (nearby.data?.nearbyBusinessList ?? []).flatMap((item) => {
          const href = venuePageHref(item);
          return href ? [{ name: item.name, href }] : [];
        });
      } catch {
        // Members-only lists 403 without a token; the browser refetches with the visitor's.
        context.queryClient.removeQueries({
          queryKey: nearbyOpts.queryKey,
          exact: true,
        });
      }
    }

    return { venue, listVenues, canonicalOrigin };
  },
  head: ({ loaderData, params }) =>
    buildWhatsNearbyHead(loaderData, params.venueId, isProduction),
});

/** The page's own loader, shown while an in-app navigation waits for the list. */
function WhatsNearbyPending() {
  const i18n = useI18N();
  return (
    <main id="main" aria-busy="true" className="h-below-navbar bg-bg-brand-secondary-default flex w-full">
      <Loader aria-label={i18n.get("loadingVenueDetails")} />
    </main>
  );
}

function WhatsNearby() {
  const { venueId: venueSlug } = Route.useParams();
  const { showAuthModal, cid, tid, venue, source } = Route.useSearch();
  const loaderData = Route.useLoaderData();
  const navigate = useNavigate();
  const router = useRouter();
  const i18n = useI18N();

  const { user, isLoading, isFetching } = useAuth();
  const isAuthLoading = isLoading || isFetching;

  const [showAuthModalState, setShowAuthModalState] = useState(showAuthModal);
  const { location } = useGeoLocation();

  // Redirect to venue details page if venue query param is present on page load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const venueQuerySlug = urlParams.get("venue");
    if (venueQuerySlug) {
      // Redirect to the actual venue details page
      navigate({ to: `/venuedetails/${venueQuerySlug}` });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run on mount

  // load this venue from the server
  // The token and location are part of the key, so signing in or moving refetches on its own.
  const {
    data,
    error,
    refetch: loadVenueDetails,
    isLoading: isDetailsLoading,
    isPlaceholderData,
  } = useWhatsNearbyRoute({
    slug: venueSlug,
    latitude:
      location?.latitude.toString() || defaultRegion.latitude.toString(),
    longitude:
      location?.longitude.toString() || defaultRegion.longitude.toString(),
    accessToken: Storage.getCurrentUserToken(),
    enabled: location ? true : false,
    placeholderData: loaderData?.venue ?? undefined,
  });

  const { showModal } = useAuthModals();

  // Retired-slug 301: the details fetch reported that this slug now redirects to a new
  // canonical slug. Swap the URL to it (preserving search params) so every follow-up
  // slug-keyed call — sync-spaces, back-to-venue, etc. — uses the live slug. replace:true
  // keeps the dead slug out of history. Mirrors the venuedetails route's SSR 301.
  useEffect(() => {
    if (error instanceof SlugRedirectError) {
      // Preserve the current search params (cid/tid/source/…). Cast per the router's
      // typed-navigate limitation, matching the venuedetails route's 301 `as any`.
      navigate({
        to: `/whats-nearby/${error.canonicalSlug}`,
        search: (prev: WhatsNearbyRouteSearch) => prev,
        replace: true,
      } as any);
    }
  }, [error, navigate]);

  useEffect(() => {
    if (showAuthModalState && !user && !isAuthLoading) {
      showModal(AuthModalScreen.EnterEmail);
      setShowAuthModalState(false);
    }
  }, [showAuthModalState, user, isAuthLoading, showModal]);

  useEffect(() => {
    if (data?.details) {
      areaMapA.track("page_view_page", {
        area_map_name: data.details.name,
        results_count: 0,
      });
    }
  }, [data?.details, cid, tid]);

  return (
    <>
      {/* Client-only: auth is always pending on the server, and a full-screen focus trap
          must never cover a list the server already rendered. */}
      <ClientOnly>
        {(isAuthLoading || isDetailsLoading) && !isPlaceholderData && !data && (
          <>
            <div role="status" aria-live="polite" className="sr-only">
              {i18n.get("loadingVenueDetails")}
            </div>
            <Loader />
          </>
        )}
      </ClientOnly>
      <main
        id="main"
        className="h-below-navbar  bg-bg-brand-secondary-default flex overflow-x-hidden w-full"
      >

        {/* Regular Desktop View - Always mounted, hidden when showing accessibility or photos detail */}
        <div
          className={cn(
            "md:flex flex-1 min-w-0",
            // (showAccessibilityDetail || showPhotosDetail) && "!hidden",
          )}
        >
          {error && !(error instanceof SlugRedirectError) && (
            <div className="max-w-[1160px] px-4 mx-auto overflow-hidden">
              <div role="alert">{error.message}</div>
            </div>
          )}

          {data && (
            <WhatsNearbyContent
              venueDetailsResponse={data}
              venueSlug={venueSlug}
              venueParam={venue}
              onClose={() => {
                const { venueDetailsSlug } = getVenueSlugs(data.details);
                window.location.href = `/venuedetails/${venueDetailsSlug}`;
              }}
              onBackToPlaces={
                source === "places"
                  ? () => {
                      // Back keeps the city page's page, filters and scroll; a direct visit has nothing to go back to.
                      if (router.history.canGoBack()) {
                        router.history.back();
                      } else {
                        void navigate({ to: "/places" });
                      }
                    }
                  : undefined
              }
              onRefresh={() => {
                setTimeout(() => loadVenueDetails(), 1000);
              }}
              cid={cid}
              tid={tid}
            />
          )}
        </div>
      </main>
    </>
  );
}
